import type { MaybeRefOrGetter, Ref } from 'vue';
import type { PaginatedQuerySource, PaginatedQueryStore } from './use-paginated-query';
import { ref } from 'vue';
import { DEFAULT_LISTING_PAGE_SIZE } from '#layers/ui/public';
import { usePaginatedQuery as createPaginatedQuery } from './use-paginated-query';

interface UsePaginatedListingOptions<TItem> {
	store: PaginatedQueryStore;
	/** RStore collection API name whose mutations should refresh the list. */
	collectionApiName: MaybeRefOrGetter<string | undefined>;
	/**
	 * Builds the query for one page.
	 *
	 * Receives the page-size ref this composable owns, so the query and the pagination controls
	 * always agree on the size without the caller wiring a second ref.
	 */
	createQuery: (pageSize: Ref<number>) => PaginatedQuerySource<TItem>;
	/** Whether page fetching and loading state are enabled. */
	enabled?: MaybeRefOrGetter<boolean>;
	/** Reactive query state that resets back to page one when it changes. */
	resetOn?: MaybeRefOrGetter<unknown>;
	/** Whether `resetOn` changes should currently reset the page. */
	resetEnabled?: MaybeRefOrGetter<boolean>;
	/** One-based page to start on, for surfaces that restore it from the URL. */
	initialPageNumber?: number;
	/** Initial rows per page. */
	initialPageSize?: number;
}

/**
 * Server-backed pagination for a listing.
 *
 * Owns the page number and page size that every listing surface repeats, and hands them to
 * `usePaginatedQuery` alongside the caller's query. Callers that need the query itself — to
 * refresh it, or to read its raw rows — get it back.
 */
export function usePaginatedListing<TItem>(options: UsePaginatedListingOptions<TItem>) {
	const pageNumber = ref(options.initialPageNumber ?? 1);
	const pageSize = ref(options.initialPageSize ?? DEFAULT_LISTING_PAGE_SIZE);
	const collectionQuery = options.createQuery(pageSize);

	const paginatedQuery = createPaginatedQuery<TItem>({
		store: options.store,
		collectionQuery,
		pageNumber,
		pageSize,
		collectionApiName: options.collectionApiName,
		enabled: options.enabled,
		resetOn: options.resetOn,
		resetEnabled: options.resetEnabled,
	});

	return {
		items: paginatedQuery.items,
		collectionQuery,
		pageNumber,
		pageSize,
		totalItemsCount: paginatedQuery.totalItemsCount,
		currentItemsCount: paginatedQuery.currentItemsCount,
		isLoading: paginatedQuery.isLoading,
		refresh: collectionQuery.refresh,
	};
}
